import { useCallback, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import DashboardLayout from "../../controls/DashboardLayout";
import DashboardNavbar from "../../controls/DashboardNavbar";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { Card } from "react-bootstrap";
import {
  Alert,
  Autocomplete,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { EmojiEvents, FileDownload, Visibility } from "@mui/icons-material";
import { getData, postData } from "../../Utils/Http/api";
import { fetchUsuarios } from "../../Utils/Usuarios";
import RankingDisparador from "./RankingDisparador";
import DetalleGestorInforme from "./DetalleGestorInforme";
import {
  CabeceraOrdenable,
  MODALIDADES,
  PAUSA_INMEDIATA_MIN,
  PORCENTAJE_NO_VUELVEN_ALTO,
  TIEMPO_MUERTO_ALTO_MIN,
  minutosLegibles,
  numero,
  ordenarFilas,
  useOrdenTabla,
} from "./informeDisparadorComun";

// Las fechas llegan con la hora del servidor, sin zona: se muestran tal cual.
const fechaLegible = (valor) => {
  if (!valor) return "-";
  const fecha = new Date(valor);
  return Number.isNaN(fecha.getTime()) ? "-" : fecha.toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
};

// Señal a revisar: texto en ámbar.
function Cifra({ valor, alerta = false, detalle }) {
  return (
    <MDBox lineHeight={1.2}>
      <MDTypography variant="button" fontWeight={alerta ? "bold" : "medium"} sx={alerta ? { color: "#b26a00" } : undefined}>
        {valor}
      </MDTypography>
      {detalle && (
        <MDTypography variant="caption" color="text" display="block">
          {detalle}
        </MDTypography>
      )}
    </MDBox>
  );
}

const ESTADOS_PROPUESTA = {
  ACTIVA: { label: "Activa", color: "info" },
  PAUSADA: { label: "Pausada", color: "warning" },
  FINALIZADA: { label: "Finalizada", color: "success" },
  LIBERADA: { label: "Liberada", color: "default" },
};

// Por qué valor se ordena cada columna al tocar su título (clave de columna → valor de la fila). Las fechas se comparan como fecha.
const marcaDeTiempo = (valor) => (valor ? new Date(valor).getTime() : null);

const ORDEN_RESUMEN = {
  gestor: (g) => g.nombre,
  propuestas: (g) => g.propuestasAsignadas,
  tiempo: (g) => g.resolucionPromedioMin,
  pausas: (g) => g.pausas,
  tiempoMuerto: (g) => g.tiempoMuertoTotalMin,
  gestiones: (g) => g.gestiones,
  noVuelven: (g) => g.porcentajeNoVuelven,
  dias: (g) => g.diasConActividad,
  horario: (g) => g.primerMovimientoPromedio,
  casos: (g) => g.casosAbiertos,
};

const ORDEN_PROPUESTAS = {
  gestor: (p) => p.gestor,
  caso: (p) => p.idCaso,
  estado: (p) => ESTADOS_PROPUESTA[p.estado]?.label ?? p.estado,
  asignada: (p) => marcaDeTiempo(p.asignadaEl),
  finalizada: (p) => marcaDeTiempo(p.finalizadaEl),
  resolucion: (p) => p.resolucionMin,
  pausa: (p) => p.pausaMin,
  efectivo: (p) => p.efectivoMin,
  evento: (p) => p.eventoFinal,
};

const ORDEN_INACTIVOS = {
  gestor: (c) => c.gestor,
  caso: (c) => c.idCaso,
  sujeto: (c) => c.nombreSujeto,
  monto: (c) => c.monto,
  vencimiento: (c) => marcaDeTiempo(c.fechaVencimiento),
};

function InformeDisparadorListar() {
  const [desde, setDesde] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [hasta, setHasta] = useState(dayjs().format("YYYY-MM-DD"));
  const [usuarios, setUsuarios] = useState([]);
  const [gestor, setGestor] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [propuestas, setPropuestas] = useState([]);
  const [inactivos, setInactivos] = useState(null);
  const [solapa, setSolapa] = useState(0);
  // Orden de cada tabla, que se mantiene al cambiar de solapa o volver a generar el informe.
  const ordenResumen = useOrdenTabla();
  const ordenPropuestas = useOrdenTabla();
  const ordenInactivos = useOrdenTabla();
  const [cargando, setCargando] = useState(false);
  const [leyendaAbierta, setLeyendaAbierta] = useState(false);
  const [rankingAbierto, setRankingAbierto] = useState(false);
  // Gestor cuyo detalle (qué significa cada cifra de su fila) se está viendo.
  const [gestorDetalle, setGestorDetalle] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });

  const aviso = (msg, severity = "success") => setSnackbar({ open: true, msg, severity });
  const mensajeDe = (ex, porDefecto) => ex?.detail || ex?.message || porDefecto;

  useEffect(() => {
    fetchUsuarios().then((lista) => setUsuarios(lista.filter((u) => !u.esTestigo)));
  }, []);

  const generar = useCallback(async () => {
    if (!desde || !hasta || desde > hasta) {
      aviso("Revisá las fechas: «desde» no puede ser posterior a «hasta»", "warning");
      return;
    }
    setCargando(true);
    try {
      const filtro = { desde, hasta, idUsuario: gestor?.idUsuario ?? 0 };
      const [res, props, inact] = await Promise.all([
        postData("/InformeDisparadorResumen", filtro),
        postData("/InformeDisparadorPropuestas", filtro),
        getData("/InformeDisparadorCasosInactivos"),
      ]);
      setResumen(res?.value ?? null);
      setPropuestas(Array.isArray(props?.value) ? props.value : []);
      setInactivos(inact?.value ?? null);
    } catch (ex) {
      console.error("Error generando el informe:", ex);
      aviso(mensajeDe(ex, "No se pudo generar el informe"), "error");
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, gestor]);

  // La tabla de posiciones usa el período que está en los campos, no hace falta haber generado el informe.
  const abrirRanking = () => {
    if (!desde || !hasta || desde > hasta) {
      aviso("Revisá las fechas: «desde» no puede ser posterior a «hasta»", "warning");
      return;
    }
    setRankingAbierto(true);
  };

  const exportarExcel = async () => {
    if (!resumen) return;
    try {
      const libro = new ExcelJS.Workbook();

      const hojaResumen = libro.addWorksheet("Resumen por gestor");
      hojaResumen.columns = [
        { header: "Gestor", key: "nombre", width: 28 },
        { header: "Activo", key: "activo", width: 8 },
        { header: "Modalidad", key: "modalidad", width: 13 },
        { header: "Gestiones mínimas por día", key: "minimo", width: 14 },
        { header: "Propuestas asignadas", key: "asignadas", width: 12 },
        { header: "Finalizadas", key: "finalizadas", width: 11 },
        { header: "Liberadas", key: "liberadas", width: 10 },
        { header: "En curso", key: "enCurso", width: 9 },
        { header: "Resolución promedio (min)", key: "resolucion", width: 14 },
        { header: "Tiempo efectivo promedio (min)", key: "efectivo", width: 14 },
        { header: "Pausas", key: "pausas", width: 8 },
        { header: "Pausa total (min)", key: "pausaTotal", width: 12 },
        { header: "Pausas inmediatas", key: "pausasInmediatas", width: 11 },
        { header: "Resoluciones rápidas", key: "rapidas", width: 11 },
        { header: "Tiempo muerto total (min)", key: "muertoTotal", width: 13 },
        { header: "Tiempo muerto máximo (min)", key: "muertoMax", width: 13 },
        { header: "Gestiones", key: "gestiones", width: 10 },
        { header: "Gestiones en propuesta", key: "enPropuesta", width: 12 },
        { header: "Gestiones fuera de propuesta", key: "fuera", width: 13 },
        { header: "Eventos que no vuelven", key: "noVuelven", width: 12 },
        { header: "% que no vuelven", key: "pctNoVuelven", width: 11 },
        { header: "Días con actividad", key: "diasActividad", width: 11 },
        { header: "Días bajo el mínimo", key: "diasBajo", width: 11 },
        { header: "Días hábiles sin actividad", key: "diasSin", width: 12 },
        { header: "Primer movimiento (promedio)", key: "primero", width: 13 },
        { header: "Último movimiento (promedio)", key: "ultimo", width: 13 },
        { header: "Casos abiertos", key: "abiertos", width: 10 },
        { header: "Casos sin gestionar", key: "sinGestionar", width: 11 },
      ];
      resumen.gestores.forEach((g) =>
        hojaResumen.addRow({
          nombre: g.nombre, activo: g.activo ? "Sí" : "No", modalidad: MODALIDADES[g.modalidad] ?? "", minimo: g.gestionesMinimasDia,
          asignadas: g.propuestasAsignadas, finalizadas: g.finalizadas, liberadas: g.liberadas, enCurso: g.enCurso,
          resolucion: g.resolucionPromedioMin, efectivo: g.efectivoPromedioMin, pausas: g.pausas, pausaTotal: g.pausaTotalMin,
          pausasInmediatas: g.pausasInmediatas, rapidas: g.resolucionesRapidas, muertoTotal: g.tiempoMuertoTotalMin,
          muertoMax: g.tiempoMuertoMaxMin, gestiones: g.gestiones, enPropuesta: g.gestionesEnPropuesta, fuera: g.gestionesFuera,
          noVuelven: g.eventosQueNoVuelven, pctNoVuelven: g.porcentajeNoVuelven, diasActividad: g.diasConActividad,
          diasBajo: g.diasBajoMinimo, diasSin: g.diasSinActividad, primero: g.primerMovimientoPromedio, ultimo: g.ultimoMovimientoPromedio,
          abiertos: g.casosAbiertos, sinGestionar: g.casosSinGestionar,
        })
      );
      hojaResumen.getRow(1).font = { bold: true };

      const hojaPropuestas = libro.addWorksheet("Propuestas");
      hojaPropuestas.columns = [
        { header: "Gestor", key: "gestor", width: 28 }, { header: "Caso", key: "caso", width: 10 }, { header: "Estado", key: "estado", width: 12 },
        { header: "Asignada", key: "asignada", width: 18 }, { header: "Finalizada", key: "finalizada", width: 18 },
        { header: "Resolución (min)", key: "resolucion", width: 14 }, { header: "Pausa (min)", key: "pausa", width: 11 },
        { header: "Tiempo efectivo (min)", key: "efectivo", width: 14 }, { header: "Puntaje", key: "puntaje", width: 9 },
        { header: "Evento final", key: "evento", width: 48 }, { header: "El caso vuelve a proponerse", key: "vuelve", width: 14 },
      ];
      propuestas.forEach((p) =>
        hojaPropuestas.addRow({
          gestor: p.gestor, caso: p.idCaso, estado: ESTADOS_PROPUESTA[p.estado]?.label ?? p.estado, asignada: fechaLegible(p.asignadaEl),
          finalizada: fechaLegible(p.finalizadaEl), resolucion: p.resolucionMin, pausa: p.pausaMin, efectivo: p.efectivoMin, puntaje: p.puntaje,
          evento: p.eventoFinal ?? "", vuelve: p.eventoFinalVuelve === null || p.eventoFinalVuelve === undefined ? "" : p.eventoFinalVuelve ? "Sí" : "No",
        })
      );
      hojaPropuestas.getRow(1).font = { bold: true };

      if (inactivos) {
        const hojaInactivos = libro.addWorksheet("Casos de gestores inactivos");
        hojaInactivos.columns = [
          { header: "Gestor desactivado", key: "gestor", width: 28 }, { header: "Caso", key: "caso", width: 10 },
          { header: "Sujeto", key: "sujeto", width: 40 }, { header: "Monto", key: "monto", width: 14 }, { header: "Vencimiento", key: "vence", width: 14 },
        ];
        (inactivos.casos ?? []).forEach((c) =>
          hojaInactivos.addRow({ gestor: c.gestor, caso: c.idCaso, sujeto: c.nombreSujeto, monto: c.monto, vence: fechaLegible(c.fechaVencimiento).split(",")[0] })
        );
        hojaInactivos.getRow(1).font = { bold: true };
      }

      const buffer = await libro.xlsx.writeBuffer();
      const url = window.URL.createObjectURL(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = `Informe_Gestion_Disparador_${desde.replaceAll("-", "")}_${hasta.replaceAll("-", "")}.xlsx`;
      enlace.click();
      window.URL.revokeObjectURL(url);
    } catch (ex) {
      console.error("Error exportando el informe:", ex);
      aviso("No se pudo exportar el informe", "error");
    }
  };

  // Título de una columna; con `clave` se puede tocar para ordenar la tabla (`tabla` es el useOrdenTabla de esa tabla).
  const cabecera = (texto, tabla, clave) => <CabeceraOrdenable texto={texto} tabla={tabla} clave={clave} />;

  const tablaResumen = (
    <TableContainer sx={{ maxHeight: "65vh" }}>
      <Table size="small" stickyHeader>
        <TableHead sx={{ display: "table-header-group", p: 0 }}>
          <TableRow>
            {cabecera("Gestor", ordenResumen, "gestor")}
            {cabecera("Propuestas", ordenResumen, "propuestas")}
            {cabecera("Tiempo por propuesta", ordenResumen, "tiempo")}
            {cabecera("Pausas", ordenResumen, "pausas")}
            {cabecera("Tiempo muerto", ordenResumen, "tiempoMuerto")}
            {cabecera("Gestiones", ordenResumen, "gestiones")}
            {cabecera("No vuelven", ordenResumen, "noVuelven")}
            {cabecera("Días", ordenResumen, "dias")}
            {cabecera("Horario promedio", ordenResumen, "horario")}
            {cabecera("Casos hoy", ordenResumen, "casos")}
            {cabecera("Detalle", ordenResumen)}
          </TableRow>
        </TableHead>
        <TableBody>
          {ordenarFilas(resumen?.gestores ?? [], ordenResumen[0], ORDEN_RESUMEN).map((g) => (
            <TableRow key={g.idUsuario} hover>
              <TableCell>
                <MDBox lineHeight={1.2}>
                  <MDTypography variant="button" fontWeight="bold">
                    {g.nombre}
                  </MDTypography>
                  <MDBox display="flex" gap={0.5} mt={0.3} flexWrap="wrap">
                    <Chip size="small" label={MODALIDADES[g.modalidad] ?? "Voluntario"} />
                    {!g.activo && <Chip size="small" color="error" label="Desactivado" />}
                  </MDBox>
                </MDBox>
              </TableCell>
              <TableCell>
                <Cifra valor={numero(g.propuestasAsignadas)} detalle={`fin. ${g.finalizadas} · lib. ${g.liberadas} · en curso ${g.enCurso}`} />
              </TableCell>
              <TableCell>
                <Cifra valor={`Resolución ${minutosLegibles(g.resolucionPromedioMin)}`} detalle={`Efectivo ${minutosLegibles(g.efectivoPromedioMin)}`} />
              </TableCell>
              <TableCell>
                <Cifra
                  valor={`${numero(g.pausas)} · ${minutosLegibles(g.pausaTotalMin)}`}
                  alerta={g.pausasInmediatas > 0}
                  detalle={g.pausasInmediatas > 0 ? `${g.pausasInmediatas} inmediatas` : "ninguna inmediata"}
                />
              </TableCell>
              <TableCell>
                <Cifra
                  valor={minutosLegibles(g.tiempoMuertoTotalMin)}
                  alerta={g.tiempoMuertoTotalMin >= TIEMPO_MUERTO_ALTO_MIN}
                  detalle={`máx. ${minutosLegibles(g.tiempoMuertoMaxMin)}`}
                />
              </TableCell>
              <TableCell>
                <Cifra valor={numero(g.gestiones)} detalle={`en propuesta ${g.gestionesEnPropuesta} · fuera ${g.gestionesFuera}`} />
              </TableCell>
              <TableCell>
                <Cifra
                  valor={g.porcentajeNoVuelven === null || g.porcentajeNoVuelven === undefined ? "-" : `${g.porcentajeNoVuelven}%`}
                  alerta={(g.porcentajeNoVuelven ?? 0) >= PORCENTAJE_NO_VUELVEN_ALTO}
                  detalle={`${g.eventosQueNoVuelven} eventos${g.resolucionesRapidas > 0 ? ` · ${g.resolucionesRapidas} rápidas` : ""}`}
                />
              </TableCell>
              <TableCell>
                <Cifra
                  valor={`${g.diasConActividad} con actividad`}
                  alerta={g.diasBajoMinimo > 0}
                  detalle={`${g.diasBajoMinimo} bajo el mínimo${g.gestionesMinimasDia > 0 ? ` (${g.gestionesMinimasDia}/día)` : ""} · ${g.diasSinActividad} sin actividad`}
                />
              </TableCell>
              <TableCell>
                <Cifra valor={g.primerMovimientoPromedio ? `${g.primerMovimientoPromedio} – ${g.ultimoMovimientoPromedio ?? "-"}` : "-"} />
              </TableCell>
              <TableCell>
                <Cifra valor={`${numero(g.casosAbiertos)} abiertos`} detalle={`${numero(g.casosSinGestionar)} sin gestionar`} />
              </TableCell>
              <TableCell>
                <Tooltip title="Ver qué significa cada cifra de este gestor">
                  <IconButton size="small" color="info" aria-label={`Ver el detalle de ${g.nombre}`} onClick={() => setGestorDetalle(g)}>
                    <Visibility />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {resumen && resumen.gestores.length === 0 && (
            <TableRow>
              <TableCell colSpan={11}>
                <MDTypography variant="button" color="text">
                  No hay propuestas ni gestiones en el período.
                </MDTypography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const tablaPropuestas = (
    <>
      {propuestas.length >= 1000 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Se muestran las 1.000 propuestas más recientes del período. Acotá las fechas o elegí un gestor para ver el resto.
        </Alert>
      )}
      <TableContainer sx={{ maxHeight: "65vh" }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ display: "table-header-group", p: 0 }}>
            <TableRow>
              {cabecera("Gestor", ordenPropuestas, "gestor")}
              {cabecera("Caso", ordenPropuestas, "caso")}
              {cabecera("Estado", ordenPropuestas, "estado")}
              {cabecera("Asignada", ordenPropuestas, "asignada")}
              {cabecera("Finalizada", ordenPropuestas, "finalizada")}
              {cabecera("Resolución", ordenPropuestas, "resolucion")}
              {cabecera("Pausa", ordenPropuestas, "pausa")}
              {cabecera("Efectivo", ordenPropuestas, "efectivo")}
              {cabecera("Evento con que se cerró", ordenPropuestas, "evento")}
            </TableRow>
          </TableHead>
          <TableBody>
            {ordenarFilas(propuestas, ordenPropuestas[0], ORDEN_PROPUESTAS).map((p) => (
              <TableRow key={p.idCasoPropuesta} hover>
                <TableCell>{p.gestor}</TableCell>
                <TableCell>#{p.idCaso}</TableCell>
                <TableCell>
                  <Chip size="small" color={ESTADOS_PROPUESTA[p.estado]?.color ?? "default"} label={ESTADOS_PROPUESTA[p.estado]?.label ?? p.estado} />
                </TableCell>
                <TableCell>{fechaLegible(p.asignadaEl)}</TableCell>
                <TableCell>{fechaLegible(p.finalizadaEl)}</TableCell>
                <TableCell>{minutosLegibles(p.resolucionMin)}</TableCell>
                <TableCell>{p.pausaMin > 0 ? minutosLegibles(p.pausaMin) : "-"}</TableCell>
                <TableCell>{minutosLegibles(p.efectivoMin)}</TableCell>
                <TableCell>
                  {p.eventoFinal ?? "-"}
                  {p.eventoFinalVuelve === false && <Chip size="small" color="warning" label="no vuelve" sx={{ ml: 1 }} />}
                </TableCell>
              </TableRow>
            ))}
            {propuestas.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <MDTypography variant="button" color="text">
                    No hay propuestas en el período.
                  </MDTypography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const tablaInactivos = (
    <>
      <Alert severity={inactivos?.total > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
        {inactivos?.total > 0
          ? `Hay ${numero(inactivos.total)} casos abiertos de gestores desactivados. No se le proponen a nadie hasta que se los reasigne con «Cambiar gestor».`
          : "Ningún gestor desactivado tiene casos abiertos."}
      </Alert>
      {inactivos?.total > 0 && (
        <>
          <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
            {inactivos.porGestor.map((g) => (
              <Chip key={g.idUsuario} label={`${g.gestor}: ${numero(g.casos)}`} color="warning" variant="outlined" />
            ))}
          </MDBox>
          {inactivos.truncado && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Se listan los primeros {inactivos.casos.length}. El total por gestor está arriba.
            </Alert>
          )}
          <TableContainer sx={{ maxHeight: "60vh" }}>
            <Table size="small" stickyHeader>
              <TableHead sx={{ display: "table-header-group", p: 0 }}>
                <TableRow>
                  {cabecera("Gestor desactivado", ordenInactivos, "gestor")}
                  {cabecera("Caso", ordenInactivos, "caso")}
                  {cabecera("Sujeto", ordenInactivos, "sujeto")}
                  {cabecera("Monto", ordenInactivos, "monto")}
                  {cabecera("Vencimiento", ordenInactivos, "vencimiento")}
                </TableRow>
              </TableHead>
              <TableBody>
                {ordenarFilas(inactivos.casos, ordenInactivos[0], ORDEN_INACTIVOS).map((c) => (
                  <TableRow key={c.idCaso} hover>
                    <TableCell>{c.gestor}</TableCell>
                    <TableCell>#{c.idCaso}</TableCell>
                    <TableCell>{c.nombreSujeto}</TableCell>
                    <TableCell>{(c.monto ?? 0).toLocaleString("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 })}</TableCell>
                    <TableCell>{fechaLegible(c.fechaVencimiento).split(",")[0]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="secondary" borderRadius="lg" coloredShadow="light">
                <MDTypography variant="h6" color="white">
                  Gestión de Casos Asignados con Prioridad
                </MDTypography>
                <MDTypography variant="button" color="white" fontWeight="regular">
                  Disparador Automático de Gestiones: tiempos, pausas, tiempo muerto y casos que nadie está gestionando.
                </MDTypography>
              </MDBox>

              <MDBox pt={3} pb={3} px={2}>
                <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
                  <TextField size="small" type="date" label="Desde" value={desde} onChange={(e) => setDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
                  <TextField size="small" type="date" label="Hasta" value={hasta} onChange={(e) => setHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
                  <Autocomplete
                    size="small"
                    options={usuarios}
                    getOptionLabel={(u) => u.nombreCompleto || ""}
                    value={gestor}
                    onChange={(e, valor) => setGestor(valor)}
                    renderInput={(params) => <TextField {...params} label="Gestor (todos si queda vacío)" />}
                    sx={{ width: 300 }}
                  />
                  <MDButton variant="gradient" color="info" onClick={generar} disabled={cargando}>
                    {cargando ? <CircularProgress size={18} color="inherit" /> : "Generar"}
                  </MDButton>
                  <MDButton variant="contained" color="success" onClick={exportarExcel} disabled={!resumen || cargando} startIcon={<FileDownload />}>
                    Excel
                  </MDButton>
                  <MDButton variant="contained" color="warning" onClick={abrirRanking} disabled={cargando} startIcon={<EmojiEvents />}>
                    Tabla de posiciones
                  </MDButton>
                  <MDButton variant="text" color="info" onClick={() => setLeyendaAbierta((v) => !v)}>
                    {leyendaAbierta ? "Ocultar cómo leerlo" : "Cómo leer el informe"}
                  </MDButton>
                </MDBox>

                <Collapse in={leyendaAbierta}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <MDBox component="ul" m={0} pl={2}>
                      <li><strong>Resolución:</strong> desde que se asigna el caso hasta que se carga el evento (solo propuestas finalizadas). <strong>Efectivo:</strong> la resolución sin las pausas.</li>
                      <li><strong>Pausas inmediatas:</strong> pausó el caso en los primeros {PAUSA_INMEDIATA_MIN} minutos de asignado (señal de querer evitarlo).</li>
                      <li><strong>Tiempo muerto:</strong> el tiempo entre cerrar un caso y recibir el siguiente, el mismo día.</li>
                      <li><strong>Gestiones:</strong> eventos con estado cargados en el período (sin el evento inicial). «Fuera» son las hechas durante una pausa o sin Disparador.</li>
                      <li><strong>No vuelven:</strong> porcentaje de gestiones con tipos de evento que sacan el caso de la cola (inubicable, excluido…). Si es muy alto puede indicar salidas fáciles; «rápidas» son resoluciones de menos de un minuto de trabajo efectivo.</li>
                      <li><strong>Días:</strong> con actividad (gestiones o propuestas), bajo el mínimo de gestiones por día y hábiles sin actividad (incluye ausencias y feriados).</li>
                      <li><strong>Horario promedio:</strong> promedio, entre los días con actividad, de la hora del primer y del último movimiento del día.</li>
                      <li><strong>Casos hoy:</strong> abiertos hoy y cuántos nunca se gestionaron (solo tienen el evento inicial). No dependen del período.</li>
                      <li><strong>Ordenar:</strong> tocá el título de una columna para ordenar la tabla por ella (una vez de menor a mayor, otra de mayor a menor y la tercera vuelve al orden original). Lo que no tiene dato queda siempre al final.</li>
                      <li>Los números en <span style={{ color: "#b26a00", fontWeight: "bold" }}>naranja</span> son señales para mirar con más atención; no son sanciones.</li>
                      <li>El ojo <Visibility fontSize="inherit" sx={{ verticalAlign: "middle" }} /> del final de cada fila explica, con los números de ese gestor, qué significa cada cifra.</li>
                    </MDBox>
                  </Alert>
                </Collapse>

                {!resumen && !cargando && (
                  <Alert severity="info">Elegí el período y tocá «Generar».</Alert>
                )}

                {resumen && (
                  <>
                    <Tabs value={solapa} onChange={(e, v) => setSolapa(v)} sx={{ mb: 2 }}>
                      <Tab label="Resumen por gestor" />
                      <Tab label={`Propuestas (${propuestas.length >= 1000 ? "1000+" : propuestas.length})`} />
                      <Tab label={`Casos de gestores inactivos${inactivos?.total ? ` (${numero(inactivos.total)})` : ""}`} />
                    </Tabs>
                    {solapa === 0 && tablaResumen}
                    {solapa === 1 && tablaPropuestas}
                    {solapa === 2 && tablaInactivos}
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <RankingDisparador open={rankingAbierto} onClose={() => setRankingAbierto(false)} desde={desde} hasta={hasta} />

      <DetalleGestorInforme open={Boolean(gestorDetalle)} onClose={() => setGestorDetalle(null)} gestor={gestorDetalle} desde={desde} hasta={hasta} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default InformeDisparadorListar;
